import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Stack,
  Group,
  Title,
  Button,
  Badge,
  Select,
  TextInput,
  Alert,
  Skeleton,
} from '@mantine/core'
import {
  IconRefresh,
  IconFileText,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconMail,
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { fetchOrgTree, exportOrgTreePDF } from '@/api/finopsOrgTree'
import { exportOrgTreeServer } from '@/api/finops'
import { OrgTreeTable } from '@/components/finops/OrgTreeTable'
import { EmailExportModal } from '@/components/finops/EmailExportModal'

/**
 * Retorna o mês corrente em formato `YYYY-MM` (UTC).
 */
const currentYm = () => new Date().toISOString().slice(0, 7)

/**
 * Página `/finops/relatorios/organizacao`.
 *
 * Apresenta o relatório FinOps Org Tree (ROOT → OU → ACCOUNT) com custos
 * por mês, filtros (busca/ordenação), exportações (CSV / XLS / PDF) e
 * envio por email. Default lê do snapshot materializado; "Atualizar agora"
 * dispara `refresh=athena` (até ~90s).
 */
export function RelatorioOrganizacao() {
  const [ym, setYm] = useState<string>(currentYm())
  const [refresh, setRefresh] = useState<'snapshot' | 'athena'>('snapshot')
  const [search, setSearch] = useState<string>('')
  const [sortBy, setSortBy] = useState<'name' | 'cost'>('name')
  const [emailOpen, setEmailOpen] = useState(false)

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['org-tree', ym, refresh],
    queryFn: () => fetchOrgTree({ ym, refresh }),
  })

  const onAthenaRefresh = async () => {
    setRefresh('athena')
    await refetch()
    setRefresh('snapshot')
  }

  // CSV/XLS agora são gerados NO SERVIDOR (auditado, escopado por conta) via
  // POST /reports/org-tree/export — fecha o gap do export client-side (GAP-5).
  const onServerExport = async (format: 'csv' | 'xls') => {
    if (!data) return
    try {
      await exportOrgTreeServer(format, { ym, source: refresh })
    } catch {
      notifications.show({
        title: 'Falha na exportação',
        message: 'Não foi possível gerar o arquivo no servidor. Tente novamente.',
        color: 'red',
      })
    }
  }

  const onPDF = async () => {
    const r = await exportOrgTreePDF({ ym, source: refresh })
    window.open(r.url, '_blank')
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Relatório por Organização</Title>
        <Group>
          <Badge variant="light" color="gray">
            {data?.snapshot_ts
              ? `Atualizado em ${new Date(data.snapshot_ts).toLocaleString('pt-BR')}`
              : '—'}
          </Badge>
          <Button
            leftSection={<IconRefresh size={14} />}
            variant="light"
            onClick={onAthenaRefresh}
            loading={isFetching}
          >
            Atualizar agora
          </Button>
        </Group>
      </Group>

      <Group>
        <Select
          label="Mês base"
          value={ym}
          onChange={(v) => v && setYm(v)}
          data={[currentYm()]}
        />
        <TextInput
          label="Buscar"
          placeholder="nome ou account_id"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Select
          label="Ordenar"
          value={sortBy}
          onChange={(v) => v && setSortBy(v as 'name' | 'cost')}
          data={[
            { value: 'name', label: 'Nome' },
            { value: 'cost', label: 'Custo (mais recente)' },
          ]}
        />
        <Group ml="auto" align="flex-end">
          <Button
            leftSection={<IconFileText size={14} />}
            variant="default"
            onClick={() => onServerExport('csv')}
            disabled={!data}
          >
            CSV
          </Button>
          <Button
            leftSection={<IconFileSpreadsheet size={14} />}
            variant="default"
            onClick={() => onServerExport('xls')}
            disabled={!data}
          >
            XLS
          </Button>
          <Button
            leftSection={<IconFileTypePdf size={14} />}
            variant="default"
            onClick={onPDF}
            disabled={!data}
          >
            PDF
          </Button>
          <Button
            leftSection={<IconMail size={14} />}
            variant="default"
            onClick={() => setEmailOpen(true)}
            disabled={!data}
          >
            E-mail
          </Button>
        </Group>
      </Group>

      {isLoading && <Skeleton height={300} />}
      {error && (
        <Alert color="red">Erro ao carregar — tente Atualizar agora.</Alert>
      )}
      {data && (
        <OrgTreeTable
          data={{
            months: data.months,
            currentMonthIsMtd: data.current_month_is_mtd,
            tree: data.tree,
          }}
          sortBy={sortBy}
          search={search}
        />
      )}

      <EmailExportModal
        opened={emailOpen}
        onClose={() => setEmailOpen(false)}
        ym={ym}
        source={refresh}
      />
    </Stack>
  )
}
